import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Tabs, Tab, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { columns } from './grid-columns';
import { TabPanel } from '../../components/tab-panel';
import { SaveContactComponent } from './save-contract.component';
import { Contract } from '../../models/contract';
import { contractApiKeyStore } from '../../api/contract-api-key-store';
import { useFileDownload } from '../../components/file-download';
import { UserPermissionName } from '../../models/user-permission-name';
import { useAuthorize } from '../../hooks/use-authorize';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

interface ContactsDialogProps {
  open: boolean;
  title?: string;
  contracts: Contract[];

  onClose: (contracts: Contract[]) => void;
}

export const ContractsDialog = ({ open, contracts, onClose }: ContactsDialogProps) => {
  const [value, setValue] = useState('view');
  const [newContacts, saveNewContacts] = useState<Contract[]>(contracts ?? []);
  const [editItem, setEditItem] = useState<Contract>();
  const { error, downloadFile, isLoading } = useFileDownload();

  const handleDialogClose = () => {
    onClose(newContacts);
  };

  const handleSaveContractCompleted = (value: Contract) => {
    if (value?.contractID) {
      const updatedContract = newContacts?.filter((contract) => contract?.contractID !== value?.contractID);
      saveNewContacts([...updatedContract, value]);
    }

    setEditItem(undefined);
    setValue('view');
  };

  const removeItem = (value: any) => {
    if (value?.length > 0) {
      const deletedContract = value[0];
      const updatedContract = newContacts?.filter((contract) => contract?.contractID !== deletedContract?.contractID);

      saveNewContacts(updatedContract);
    }
  };

  const handleTabsChange = (event: any, value: any) => {
    setValue(value);
  };

  const handleEdit = (value: Contract) => {
    setEditItem(value);
    setValue('edit');
  };

  const handleDownloadFile = async (data: any) => {
    downloadFile({ additionalData: data });
  };

  const isAddContractPermission = !useAuthorize([UserPermissionName?.ADD_CONTRACT_OFFICE]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={handleDialogClose}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title" sx={{ p: 0, m: 0 }}>
        <Tabs value={value} onChange={handleTabsChange}>
          <Tab value="view" label={<div style={styling.tabHeading}>VIEW CONTRACTS</div>} />
          <Tab
            value="create"
            label={<div style={styling.tabHeading}>CREATE CONTRACT</div>}
            hidden={!!editItem || isAddContractPermission}
          />
          <Tab value="edit" label={<div>EDIT CONTRACT</div>} hidden={!editItem} />
        </Tabs>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 16,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <TabPanel selectedValue={value} value="view">
          <SiTrackGrid
          fileTitle='contract'
            dataItems={newContacts}
            dataItemKey="contractID"
            columns={columns}
            onEdit={handleEdit}
            onDeletedItems={removeItem}
            deleteMutationConfig={(item) => contractApiKeyStore.deleteContract(item.contractID)}
            onDownload={(data: any) => {
              handleDownloadFile(data);
            }}
            actionPermissions={{
              edit: UserPermissionName.EDIT_CONTRACT_OFFICE,
              delete: UserPermissionName.DELETE_CONTRACT_OFFICE,
              download: UserPermissionName.DOWNLOAD_CONTRACT_OFFICE,
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            isBusy={isLoading}
          />
        </TabPanel>
        <TabPanel selectedValue={value} value="create">
          <SaveContactComponent onSaveContract={handleSaveContractCompleted} />
        </TabPanel>
        <TabPanel selectedValue={value} value="edit">
          <SaveContactComponent onSaveContract={handleSaveContractCompleted} contract={editItem} />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};
